import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';

export function BotaoEmailRecibo({ onClick, disabled, titulo }) {
    return <button type="button" className="rh-recibo-email" onClick={onClick} disabled={disabled} title={titulo} aria-label={titulo}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 6 9 7 9-7"/></svg>
    </button>;
}
async function outlookLocal(path, job) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 120000);
    try {
        const r = await fetch('http://localhost:17653' + path, {
            method: job ? 'POST' : 'GET',
            headers: { 'X-SPROD-Outlook': '1', ...(job ? { 'Content-Type': 'application/json' } : {}) },
            ...(job ? { body: JSON.stringify(job) } : {}),
            signal: controller.signal
        });
        const data = await r.json();
        if (!r.ok) throw new Error(data.error || 'O auxiliar Outlook não respondeu.');
        return data;
    } finally { clearTimeout(timer); }
}
export default function RecibosEmail({ pedido, fechar }) {
    const [preview, setPreview] = useState(null);
    const [reenviar, setReenviar] = useState(false);
    const [erro, setErro] = useState('');
    const [outlook, setOutlook] = useState('A verificar a ligação ao Outlook…');
    const [ocupado, setOcupado] = useState(false);
    const [resultados, setResultados] = useState({});
    const emCurso = useRef(false);
    const dialogo = useRef(null);
    const verificarOutlook = async () => {
        try { await outlookLocal('/status'); setOutlook(''); return true; }
        catch {
            setOutlook('Abra o auxiliar SPROD Outlook neste computador e confirme a conta financeiro@sprod.pt no Outlook clássico. Se o navegador pedir acesso à rede local, permita-o.');
            return false;
        }
    };
    useEffect(() => {
        let ativo = true;
        const anterior = document.activeElement;
        setPreview(null); setErro(''); setResultados({});
        api.post('/rh/vencimentos/email/preparar', { ...pedido, reenviar }, { timeout: 120000 })
            .then(r => { if (ativo) setPreview(r.data); })
            .catch(e => { if (ativo) setErro(e.response?.status === 404
                ? 'É necessário reiniciar o serviço do servidor para ativar o envio de recibos.'
                : e.response?.data?.error || 'Não foi possível preparar o envio.'); });
        dialogo.current?.focus();
        verificarOutlook();
        return () => { ativo = false; anterior?.focus?.(); };
    }, [pedido, reenviar]);
    const enviar = async () => {
        if (emCurso.current || !preview || preview.modo !== 'outlook') return;
        emCurso.current = true;
        setOcupado(true);
        try {
            if (!await verificarOutlook()) return;
            for (const l of preview.linhas.filter(l => !l.motivo && !resultados[l.id])) {
                setResultados(r => ({ ...r, [l.id]: 'A enviar pelo Outlook…' }));
                let reservado = false;
                try {
                    const r = await api.post('/rh/vencimentos/email/enviar', { token: preview.token, id: l.id }, { timeout: 120000 });
                    if (r.data.modo !== 'outlook' || !r.data.job) throw new Error('Atualize o serviço do servidor antes de continuar.');
                    reservado = true;
                    const resultado = await outlookLocal('/enviar', r.data.job);
                    const confirmacao = await api.post('/rh/vencimentos/email/confirmar', { token: preview.token, id: l.id, estado: resultado.estado });
                    setPreview(v => ({ ...v, linhas: v.linhas.map(row => row.id === l.id ? { ...row, historico: confirmacao.data.historico, totalEnvios: confirmacao.data.totalEnvios } : row) }));
                    setResultados(v => ({ ...v, [l.id]: resultado.estado === 'entregue_outlook'
                        ? 'Entregue ao Outlook. Consulte a Caixa de Saída ou os Itens Enviados.'
                        : resultado.error || 'Envio não concluído.' }));
                    if (resultado.estado !== 'entregue_outlook') {
                        setErro('O processo foi interrompido. Os restantes recibos não foram enviados.');
                        break;
                    }
                } catch (e) {
                    if (reservado) {
                        try { await api.post('/rh/vencimentos/email/confirmar', {token:preview.token,id:l.id,estado:'incerto'}); } catch (_) {}
                    }
                    setResultados(r => ({ ...r, [l.id]: reservado
                        ? 'Resultado por confirmar. Consulte o Outlook antes de repetir.'
                        : e.response?.data?.error || e.message || 'Não foi possível preparar o envio.' }));
                    setErro('O processo foi interrompido. Os restantes recibos não foram enviados.');
                    break;
                }
            }
        } finally { setOcupado(false); emCurso.current = false; }
    };
    const pendentes = preview?.linhas.filter(l => !l.motivo && !resultados[l.id]).length || 0;
    return createPortal(<div className="rh-email-overlay">
        <section className="rh-email-dialog" role="dialog" aria-modal="true" aria-labelledby="rh-email-titulo" tabIndex="-1" ref={dialogo}
            onKeyDown={e => {
                if (e.key === 'Escape' && !ocupado) fechar();
                if (e.key === 'Tab') {
                    const botoes = [...e.currentTarget.querySelectorAll('button:not(:disabled)')];
                    const primeiro = botoes[0], ultimo = botoes[botoes.length - 1];
                    if (e.shiftKey && (document.activeElement === primeiro || document.activeElement === dialogo.current)) { e.preventDefault(); ultimo?.focus(); }
                    else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primeiro?.focus(); }
                }
            }}>
            <h2 id="rh-email-titulo">Enviar recibos pelo Outlook</h2>
            {!preview && !erro && <p>A preparar destinatários e recibos…</p>}
            {erro && <p role="alert">{erro}</p>}
            <label style={{ display: 'block', marginBottom: 12 }}><input type="checkbox" checked={reenviar} disabled={ocupado} onChange={e => setReenviar(e.target.checked)} /> Permitir reenviar recibos já enviados</label>
            {preview && <>
                <p><strong>De:</strong> Financeiro (Sprod) &lt;financeiro@sprod.pt&gt;</p>
                <p><strong>Bcc em cada mensagem:</strong> {preview.bcc.join('; ')}</p>
                <p><strong>Assunto:</strong> {preview.subject}</p>
                <div className="rh-email-mensagem">{preview.text}</div>
                <p>Cada trabalhador recebe uma mensagem individual com os ficheiros indicados abaixo, através do Outlook deste computador.</p>
                {preview.modo !== 'outlook' && <p role="alert">Reinicie o serviço do servidor para ativar o envio pelo Outlook.</p>}
                {outlook && <p role="status">{outlook} <button type="button" disabled={ocupado} onClick={verificarOutlook}>Verificar ligação</button></p>}
                <div className="rh-email-lista" aria-live="polite">
                    {preview.linhas.map(l => <div key={l.id}>
                        <strong>{l.nome}</strong> — {l.email || 'Sem e-mail'}
                        <div>{l.ficheiros.join(', ') || 'Sem recibo'}</div>
                        <div><strong>Envios registados: {l.totalEnvios || 0}</strong></div>
                        {l.assunto && <div>Assunto deste envio: {l.assunto}</div>}
                        {(l.historico || []).length > 0 && <details>
                            <summary>Ver histórico de envios</summary>
                            <ul>{l.historico.map(h => <li key={h.via}>
                                {h.via}ª via — {new Date(h.enviado_em || h.criado_em).toLocaleString('pt-PT', { timeZone: 'Europe/Lisbon' })}
                                {' — '}{h.estado === 'enviado' ? 'Entregue ao Outlook' : 'Por confirmar'}
                                {' — '}{h.destinatario}
                            </li>)}</ul>
                        </details>}
                        <div>{resultados[l.id] || l.motivo || (l.jaEnviado ? 'Pronto para reenviar' : 'Pronto para enviar')}</div>
                    </div>)}
                </div>
            </>}
            <footer>
                <button type="button" onClick={fechar} disabled={ocupado}>{Object.keys(resultados).length ? 'Fechar' : 'Cancelar'}</button>
                <button type="button" onClick={enviar} disabled={ocupado || preview?.modo !== 'outlook' || !!outlook || !pendentes || !!erro}>
                    {ocupado ? 'A enviar pelo Outlook…' : (reenviar ? 'Enviar / reenviar pelo Outlook: ' : 'Enviar pelo Outlook: ') + pendentes + (pendentes === 1 ? ' recibo' : ' recibos')}
                </button>
            </footer>
        </section>
    </div>, document.body);
}
